// src/tabs/FinanceiroConfigSubTab.js
import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Check, X, ShieldAlert, Sparkles } from "lucide-react";
import { formatCentavos, parseCentavos } from "../utils/moneyUtils";
import { financeiroIntegracaoService } from "../services/financeiroIntegracaoService";

const ORIGENS_MAP = [
  { id: "production_orders", label: "Pedidos de Fábrica (Vendas)", tipo: "receita" },
  { id: "pdv_sales", label: "Vendas Showroom / PDV", tipo: "receita" },
  { id: "supply_purchases", label: "Compras de Suprimentos", tipo: "despesa" },
  { id: "colaboradores_historico_pagamentos", label: "Folha de Pagamento", tipo: "despesa" },
  { id: "pontuacao_pagamentos", label: "Bônus de Colaboradores", tipo: "despesa" },
  { id: "motoboy_pagamentos", label: "Fretes e Motoboy", tipo: "despesa" }
];

function RegraRow({ origem, rule, categorias, centrosCusto, onSave, onDelete }) {
  const [cat, setCat] = useState(rule?.categoria_default || "");
  const [centro, setCentro] = useState(rule?.centro_custo_default || "");

  useEffect(() => {
    if (rule) {
      setCat(rule.categoria_default || "");
      setCentro(rule.centro_custo_default || "");
    } else {
      setCat("");
      setCentro("");
    }
  }, [rule]);

  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-slate-200 rounded-xl bg-slate-50/55 hover:bg-slate-50 transition-colors shadow-sm w-full">
      <div className="flex-1">
        <h4 className="text-xs font-black text-slate-700 uppercase tracking-wide">{origem.label}</h4>
        <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wide ${
          origem.tipo === "receita" 
            ? "bg-emerald-50 text-emerald-700 border border-emerald-100" 
            : "bg-red-50 text-red-700 border border-red-100"
        }`}>
          {origem.tipo === "receita" ? "Entrada / Receita" : "Saída / Despesa"}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 shrink-0">
        <div className="w-full sm:w-48">
          <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Categoria Padrão</label>
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="w-full px-2.5 py-1.5 border border-slate-350 rounded-lg text-xs font-bold bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Nenhuma...</option>
            {categorias
              .filter((c) => c.ativo && c.tipo === origem.tipo)
              .map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
          </select>
        </div>

        <div className="w-full sm:w-48">
          <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Centro Padrão</label>
          <select
            value={centro}
            onChange={(e) => setCentro(e.target.value)}
            className="w-full px-2.5 py-1.5 border border-slate-350 rounded-lg text-xs font-bold bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Nenhum...</option>
            {centrosCusto.filter(c => c.ativo).map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2 sm:self-end">
          <button
            onClick={() => onSave(cat, centro)}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow transition-colors flex items-center justify-center h-[34px]"
          >
            Salvar
          </button>
          {origem.isAdvanced && onDelete && (
            <button
              onClick={onDelete}
              className="p-2 bg-red-55 hover:bg-red-100 text-red-600 rounded-lg border border-red-200 transition-colors flex items-center justify-center w-[34px] h-[34px]"
              title="Excluir regra avançada"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AdvancedRuleForm({ categorias, centrosCusto, onAdd }) {
  const [type, setType] = useState("sector"); // sector, category
  const [keyName, setKeyName] = useState("");
  const [cat, setCat] = useState("");
  const [centro, setCentro] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(type, keyName, cat, centro);
    setKeyName("");
    setCat("");
    setCentro("");
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 flex flex-col gap-4 w-full">
      <span className="text-[10px] font-black text-slate-500 uppercase tracking-wide block border-b border-slate-200 pb-2 mb-1">
        Nova Regra de Categorização Avançada
      </span>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Tipo de Filtro</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full px-2.5 py-1.5 border border-slate-350 rounded-lg text-xs font-semibold bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="sector">Por Setor do Insumo</option>
            <option value="category">Por Categoria do Insumo</option>
          </select>
        </div>

        <div>
          <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Nome (Ex: Pedras, polimento) *</label>
          <input
            type="text"
            required
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            className="w-full px-2.5 py-1.5 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
            placeholder={type === "sector" ? "Oficina, Pedras..." : "zirconia, embalagem..."}
          />
        </div>

        <div>
          <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Categoria Financeira</label>
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="w-full px-2.5 py-1.5 border border-slate-350 rounded-lg text-xs font-semibold bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Nenhuma...</option>
            {categorias
              .filter((c) => c.ativo && c.tipo === "despesa")
              .map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
          </select>
        </div>

        <div>
          <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Centro Padrão (Empresa)</label>
          <select
            value={centro}
            onChange={(e) => setCentro(e.target.value)}
            className="w-full px-2.5 py-1.5 border border-slate-350 rounded-lg text-xs font-semibold bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Nenhum...</option>
            {centrosCusto.filter(c => c.ativo).map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          className="flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white px-4 py-1.5 rounded-lg text-xs font-bold shadow transition-colors"
        >
          <Plus size={14} /> Adicionar Regra
        </button>
      </div>
    </form>
  );
}

export default function FinanceiroConfigSubTab({ finance }) {
  const [section, setSection] = useState("categorias"); // categorias, centros, contas, classificacoes, regras
  const [regras, setRegras] = useState([]);

  const { setLoading } = finance;

  useEffect(() => {
    if (section === "regras") {
      const fetchRegras = async () => {
        try {
          setLoading(true);
          const list = await financeiroIntegracaoService.getRegrasCategorizacao();
          setRegras(list);
        } catch (err) {
          console.warn("Erro ao buscar regras:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchRegras();
    }
  }, [section, setLoading]);

  const refreshRegras = async () => {
    try {
      const list = await financeiroIntegracaoService.getRegrasCategorizacao();
      setRegras(list);
    } catch (err) {
      console.warn("Erro ao carregar regras:", err);
    }
  };

  // Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(""); // create, edit
  const [editingId, setEditingId] = useState(null);

  // Form Fields
  const [fieldName, setFieldName] = useState("");
  // Categoria
  const [fieldTipo, setFieldTipo] = useState("despesa");
  const [fieldClassificacao, setFieldClassificacao] = useState("");
  const [fieldAtivo, setFieldAtivo] = useState(true);
  // Conta Bancária
  const [fieldBanco, setFieldBanco] = useState("");
  const [fieldAgencia, setFieldAgencia] = useState("");
  const [fieldConta, setFieldConta] = useState("");
  const [fieldContaTipo, setFieldContaTipo] = useState("corrente");
  const [fieldSaldoInicial, setFieldSaldoInicial] = useState("0,00");

  // Fechamentos e Recálculos States
  const [fechaMes, setFechaMes] = useState("7");
  const [fechaAno, setFechaAno] = useState("2026");
  const [recalcMes, setRecalcMes] = useState("7");
  const [recalcAno, setRecalcAno] = useState("2026");

  const openCreateModal = () => {
    setModalType("create");
    setFieldName("");
    setFieldTipo("despesa");
    setFieldClassificacao(finance.classificacoes[0]?.id || "");
    setFieldAtivo(true);
    setFieldBanco("");
    setFieldAgencia("");
    setFieldConta("");
    setFieldContaTipo("corrente");
    setFieldSaldoInicial("0,00");
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setModalType("edit");
    setEditingId(item.id);
    setFieldName(item.nome || "");
    setFieldAtivo(item.ativo !== undefined ? item.ativo : true);

    if (section === "categorias") {
      setFieldTipo(item.tipo || "despesa");
      setFieldClassificacao(item.classificacao || "");
    } else if (section === "contas") {
      setFieldBanco(item.banco || "");
      setFieldAgencia(item.agencia || "");
      setFieldConta(item.conta || "");
      setFieldContaTipo(item.tipo || "corrente");
      setFieldSaldoInicial(formatCentavos(item.saldo_inicial_centavos || 0).replace("R$", "").trim());
    }

    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!fieldName.trim()) return;

    try {
      if (section === "classificacoes") {
        if (modalType === "create") {
          await finance.createClassificacao(fieldName.trim());
        }
      } else if (section === "categorias") {
        const payload = {
          nome: fieldName.trim().toUpperCase(),
          tipo: fieldTipo,
          classificacao: fieldClassificacao,
          ativo: fieldAtivo,
        };
        if (modalType === "create") {
          await finance.createCategoria(payload);
        } else {
          await finance.updateCategoria(editingId, payload);
        }
      } else if (section === "centros") {
        const payload = {
          nome: fieldName.trim().toUpperCase(),
          ativo: fieldAtivo,
        };
        if (modalType === "create") {
          await finance.createCentroCusto(payload);
        } else {
          await finance.updateCentroCusto(editingId, payload);
        }
      } else if (section === "contas") {
        const payload = {
          nome: fieldName.trim().toUpperCase(),
          banco: fieldBanco.trim().toUpperCase(),
          agencia: fieldAgencia.trim(),
          conta: fieldConta.trim(),
          tipo: fieldContaTipo,
          saldo_inicial_centavos: parseCentavos(fieldSaldoInicial),
          ativo: fieldAtivo,
        };
        if (modalType === "create") {
          await finance.createContaBancaria(payload);
        } else {
          await finance.updateContaBancaria(editingId, payload);
        }
      } else if (section === "compromisso") {
        const payload = {
          nome: fieldName.trim().toUpperCase(),
          ativo: fieldAtivo,
        };
        if (modalType === "create") {
          await finance.createTipoCompromisso(fieldName.trim());
        } else {
          await finance.updateTipoCompromisso(editingId, payload);
        }
      }
      setIsModalOpen(false);
    } catch (err) {
      alert("Erro ao salvar dados: " + err.message);
    }
  };

  const handleDeleteClassificacao = async (item) => {
    if (!window.confirm(`Excluir classificação "${item.nome}"? Certifique-se de que nenhuma categoria esteja usando-a.`)) return;
    try {
      await finance.deleteClassificacao(item.id, item.nome);
    } catch (err) {
      alert("Erro ao excluir classificação. Verifique se existem vínculos.");
    }
  };

  const handleFecharSubmit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Tem certeza que deseja fechar o período ${String(fechaMes).padStart(2, "0")}/${fechaAno}?`)) return;
    try {
      finance.setLoading(true);
      await finance.fecharMes({ ano: parseInt(fechaAno, 10), mes: parseInt(fechaMes, 10) });
      alert("Período fechado com sucesso!");
    } catch (err) {
      alert("Erro ao fechar período: " + err.message);
    } finally {
      finance.setLoading(false);
    }
  };

  const handleRecalcularSubmit = async (e) => {
    e.preventDefault();
    try {
      finance.setLoading(true);
      await finance.recalcularResumoMensal(parseInt(recalcAno, 10), parseInt(recalcMes, 10));
      alert("Consolidado mensal recalculado com sucesso!");
    } catch (err) {
      alert("Erro ao recalcular consolidado: " + err.message);
    } finally {
      finance.setLoading(false);
    }
  };

  const handleReabrirClick = async (fechamento) => {
    const motivo = window.prompt(`Informe a justificativa/motivo para reabrir o período ${String(fechamento.mes).padStart(2, "0")}/${fechamento.ano} *:`);
    if (motivo === null) return;
    if (!motivo.trim()) {
      alert("Justificativa obrigatória para reabrir o período.");
      return;
    }

    try {
      finance.setLoading(true);
      await finance.reabrirMes({ ano: fechamento.ano, mes: fechamento.mes, motivo: motivo.trim() });
      alert("Período reaberto com sucesso!");
    } catch (err) {
      alert("Erro ao reabrir período: " + err.message);
    } finally {
      finance.setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-slate-50">
      
      {/* ─── SIDEBAR DE SEÇÕES ─────────────────────────────────────────── */}
      <div className="w-full md:w-56 bg-white border-b md:border-b-0 md:border-r border-slate-200 p-4 shrink-0 flex flex-row md:flex-col gap-1 overflow-x-auto [&::-webkit-scrollbar]:hidden md:overflow-x-visible">
        <button
          onClick={() => setSection("categorias")}
          className={`flex-1 md:flex-none text-left px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
            section === "categorias"
              ? "bg-blue-50 text-blue-700 shadow-sm"
              : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          Categorias
        </button>
        <button
          onClick={() => setSection("centros")}
          className={`flex-1 md:flex-none text-left px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
            section === "centros"
              ? "bg-blue-50 text-blue-700 shadow-sm"
              : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          Centros de Custo
        </button>
        <button
          onClick={() => setSection("contas")}
          className={`flex-1 md:flex-none text-left px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
            section === "contas"
              ? "bg-blue-50 text-blue-700 shadow-sm"
              : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          Contas Bancárias
        </button>
        <button
          onClick={() => setSection("classificacoes")}
          className={`flex-1 md:flex-none text-left px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
            section === "classificacoes"
              ? "bg-blue-50 text-blue-700 shadow-sm"
              : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          Classificações
        </button>
        <button
          onClick={() => setSection("regras")}
          className={`flex-1 md:flex-none text-left px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
            section === "regras"
              ? "bg-blue-50 text-blue-700 shadow-sm"
              : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          Regras de Integração
        </button>
        <button
          onClick={() => setSection("fechamentos")}
          className={`flex-1 md:flex-none text-left px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
            section === "fechamentos"
              ? "bg-blue-50 text-blue-700 shadow-sm"
              : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          Fechamentos e Resumos
        </button>
        <button
          onClick={() => setSection("compromisso")}
          className={`flex-1 md:flex-none text-left px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
            section === "compromisso"
              ? "bg-blue-50 text-blue-700 shadow-sm"
              : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          Tipos de Compromisso
        </button>
      </div>

      {/* ─── CONTEÚDO PRINCIPAL DA SEÇÃO ────────────────────────────────── */}
      <div className="flex-1 flex flex-col p-4 lg:p-6 overflow-hidden">
        
        {/* Header Seção */}
        <div className="flex justify-between items-center gap-4 mb-4 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-slate-800 uppercase tracking-wide">
              {section === "categorias" && "Gestão de Categorias"}
              {section === "centros" && "Gestão de Centros de Custo"}
              {section === "contas" && "Gestão de Contas Bancárias"}
              {section === "classificacoes" && "Classificações Financeiras"}
              {section === "regras" && "Regras de Categorização Automática"}
              {section === "fechamentos" && "Fechamentos de Período & Auditoria"}
              {section === "compromisso" && "Tipos de Compromisso"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {section === "categorias" && "Mapeie e classifique o seu fluxo de receitas e despesas."}
              {section === "centros" && "Identifique os núcleos de destino/origem dos custos operacionais."}
              {section === "contas" && "Cadastre suas contas ativas para conciliação de extratos."}
              {section === "classificacoes" && "Crie as classificações gerais do pre-seletor de categorias."}
              {section === "regras" && "Defina a categoria e centro de custo padrão sugeridos para as integrações automáticas do sistema."}
              {section === "fechamentos" && "Bloqueie lançamentos retroativos, recalcule consolidados e audite o fluxo financeiro."}
              {section === "compromisso" && "Gerencie o catálogo de tipos de compromissos recorrentes/despesas fixas."}
            </p>
          </div>
          {section !== "regras" && section !== "fechamentos" && (
            <button
              onClick={openCreateModal}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow transition-colors"
            >
              <Plus size={16} /> Adicionar Novo
            </button>
          )}
        </div>

        {/* Grade/Tabela */}
        <div className="flex-1 bg-white border border-slate-200 rounded-xl overflow-y-auto shadow-sm">
          {section === "classificacoes" && (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Nome da Classificação</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 text-right w-24">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {finance.classificacoes.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-sm font-semibold text-slate-700">{c.nome}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDeleteClassificacao(c)}
                        className="text-slate-400 hover:text-red-600 transition-colors p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {finance.classificacoes.length === 0 && (
                  <tr>
                    <td colSpan="2" className="px-4 py-8 text-center text-xs font-medium text-slate-400">
                      Nenhuma classificação cadastrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {section === "categorias" && (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Categoria</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Tipo</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Classificação</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Status</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 text-right w-24">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {finance.categorias.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-sm font-bold text-slate-800">{c.nome}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide ${
                        c.tipo === "receita" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                      }`}>
                        {c.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-slate-500">
                      {c.expand?.classificacao?.nome || <span className="text-red-400">Não classificada</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block w-2 h-2 rounded-full ${c.ativo ? "bg-emerald-500" : "bg-slate-350"}`} />
                      <span className="text-xs font-medium text-slate-500 ml-1.5">{c.ativo ? "Ativo" : "Inativo"}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openEditModal(c)}
                        className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                      >
                        <Edit2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {finance.categorias.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-xs font-medium text-slate-400">
                      Nenhuma categoria cadastrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {section === "centros" && (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Centro de Custo</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Status</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 text-right w-24">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {finance.centrosCusto.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-sm font-bold text-slate-700">{c.nome}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block w-2 h-2 rounded-full ${c.ativo ? "bg-emerald-500" : "bg-slate-300"}`} />
                      <span className="text-xs font-medium text-slate-500 ml-1.5">{c.ativo ? "Ativo" : "Inativo"}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openEditModal(c)}
                        className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                      >
                        <Edit2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {finance.centrosCusto.length === 0 && (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-xs font-medium text-slate-400">
                      Nenhum centro de custo cadastrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {section === "contas" && (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Nome Identificador</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Banco / Dados</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Tipo</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Saldo Inicial</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Status</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 text-right w-24">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {finance.contasBancarias.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-sm font-bold text-slate-800">{c.nome}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {c.banco && `${c.banco}`} {c.agencia && `| Ag: ${c.agencia}`} {c.conta && `| Cc: ${c.conta}`}
                      {!c.banco && !c.agencia && !c.conta && "-"}
                    </td>
                    <td className="px-4 py-3 text-xs font-bold capitalize text-slate-600">{c.tipo || "corrente"}</td>
                    <td className="px-4 py-3 text-xs font-bold font-mono text-slate-600">{formatCentavos(c.saldo_inicial_centavos || 0)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block w-2 h-2 rounded-full ${c.ativo ? "bg-emerald-500" : "bg-slate-300"}`} />
                      <span className="text-xs font-medium text-slate-500 ml-1.5">{c.ativo ? "Ativa" : "Inativa"}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openEditModal(c)}
                        className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                      >
                        <Edit2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {finance.contasBancarias.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-xs font-medium text-slate-400">
                      Nenhuma conta bancária cadastrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {section === "regras" && (
            <div className="p-4 md:p-6 space-y-6">
              
              <div>
                <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider mb-3">Regras de Integração Padrão</h3>
                <div className="space-y-4">
                  {ORIGENS_MAP.map((orig) => {
                    const r = regras.find((x) => x.origem === orig.id);
                    return (
                      <RegraRow
                        key={orig.id}
                        origem={orig}
                        rule={r}
                        categorias={finance.categorias}
                        centrosCusto={finance.centrosCusto}
                        onSave={async (catId, centroId) => {
                          try {
                            finance.setLoading(true);
                            await financeiroIntegracaoService.saveRegraCategorizacao(orig.id, catId, centroId);
                            await refreshRegras();
                            alert("Regra de categorização padrão atualizada com sucesso!");
                          } catch (err) {
                            alert("Erro ao salvar regra: " + err.message);
                          } finally {
                            finance.setLoading(false);
                          }
                        }}
                      />
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider mb-2">Regras Avançadas por Insumo (Setores / Categorias)</h3>
                <p className="text-[11px] text-slate-500 mb-4">
                  Direcione as despesas de compras de suprimentos automaticamente de acordo com o Setor ou Categoria operacional do insumo comprado (ex: Zircônia ➔ Matéria Prima, Manutenção ➔ Serviços).
                </p>

                {/* Form Cadastro Regra Avançada */}
                <AdvancedRuleForm
                  categorias={finance.categorias}
                  centrosCusto={finance.centrosCusto}
                  onAdd={async (type, keyName, catId, centroId) => {
                    const cleanKey = String(keyName).toLowerCase().trim();
                    if (!cleanKey) {
                      alert("Digite o nome do setor ou categoria.");
                      return;
                    }
                    const origId = `supply_purchases:${type}:${cleanKey}`;
                    try {
                      finance.setLoading(true);
                      await financeiroIntegracaoService.saveRegraCategorizacao(origId, catId, centroId);
                      await refreshRegras();
                      alert("Regra avançada criada com sucesso!");
                    } catch (err) {
                      alert("Erro ao salvar regra avançada: " + err.message);
                    } finally {
                      finance.setLoading(false);
                    }
                  }}
                />

                {/* Listagem de Regras Avançadas */}
                <div className="mt-6 space-y-4">
                  {regras
                    .filter((x) => x.origem.startsWith("supply_purchases:sector:") || x.origem.startsWith("supply_purchases:category:"))
                    .map((r) => {
                      const isSector = r.origem.startsWith("supply_purchases:sector:");
                      const name = r.origem
                        .replace("supply_purchases:sector:", "")
                        .replace("supply_purchases:category:", "")
                        .toUpperCase();

                      return (
                        <RegraRow
                          key={r.id}
                          origem={{
                            id: r.origem,
                            label: `${isSector ? "Setor Insumo" : "Categoria Insumo"}: ${name}`,
                            tipo: "despesa",
                            isAdvanced: true,
                          }}
                          rule={r}
                          categorias={finance.categorias}
                          centrosCusto={finance.centrosCusto}
                          onSave={async (catId, centroId) => {
                            try {
                              finance.setLoading(true);
                              await financeiroIntegracaoService.saveRegraCategorizacao(r.origem, catId, centroId);
                              await refreshRegras();
                              alert("Regra avançada atualizada!");
                            } catch (err) {
                              alert("Erro: " + err.message);
                            } finally {
                              finance.setLoading(false);
                            }
                          }}
                          onDelete={async () => {
                            if (!window.confirm(`Deseja mesmo excluir a regra para ${name}?`)) return;
                            try {
                              finance.setLoading(true);
                              await financeiroIntegracaoService.deleteRegraCategorizacao(r.id);
                              await refreshRegras();
                              alert("Regra avançada removida!");
                            } catch (err) {
                              alert("Erro: " + err.message);
                            } finally {
                              finance.setLoading(false);
                            }
                          }}
                        />
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {section === "fechamentos" && (
            <div className="p-6 space-y-6">
              
              {/* Modo de Bloqueio Rígido */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs">
                <div className="flex-1">
                  <h4 className="text-xs font-black text-slate-700 uppercase tracking-wide">Bloqueio Rígido de Meses Fechados</h4>
                  <p className="text-[11px] text-slate-500 mt-1 max-w-xl">
                    Se ativado, impede fisicamente qualquer inclusão, liquidação ou estorno retroativo nos meses fechados. 
                    Se desativado (recomendado para testes/homologação), exibe apenas um aviso visual e registra no log da auditoria, permitindo a gravação.
                  </p>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <span className={`text-[10px] font-black uppercase ${finance.bloqueioRigido ? "text-red-600 animate-pulse" : "text-slate-500"}`}>
                    {finance.bloqueioRigido ? "Ativado (Rígido)" : "Desativado (Flexível)"}
                  </span>
                  <button
                    type="button"
                    onClick={finance.toggleBloqueioRigido}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out outline-none ${
                      finance.bloqueioRigido ? "bg-red-600" : "bg-slate-300"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        finance.bloqueioRigido ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Controles de Fechamento de Mês */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Fechar Novo Mês */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs">
                  <h4 className="text-xs font-black text-slate-700 uppercase tracking-wide mb-3">Fechar Novo Período</h4>
                  <form onSubmit={handleFecharSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Mês</label>
                        <select
                          value={fechaMes}
                          onChange={(e) => setFechaMes(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs font-bold bg-white outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                            <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Ano</label>
                        <select
                          value={fechaAno}
                          onChange={(e) => setFechaAno(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs font-bold bg-white outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {[2025, 2026, 2027, 2028].map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs py-2 rounded-lg transition-colors uppercase tracking-wider animate-pulse"
                    >
                      Solicitar Fechamento
                    </button>
                  </form>
                </div>

                {/* Recalcular buckets de resumo */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs">
                  <h4 className="text-xs font-black text-slate-700 uppercase tracking-wide mb-3">Recalcular Acumulados</h4>
                  <p className="text-[10px] text-slate-400 mb-3">
                    Força a re-computação completa dos somatórios consolidados do Ledger para um período. Útil se houver divergência ou importações antigas.
                  </p>
                  <form onSubmit={handleRecalcularSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Mês</label>
                        <select
                          value={recalcMes}
                          onChange={(e) => setRecalcMes(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs font-bold bg-white outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                            <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Ano</label>
                        <select
                          value={recalcAno}
                          onChange={(e) => setRecalcAno(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs font-bold bg-white outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {[2025, 2026, 2027, 2028].map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs py-2 rounded-lg transition-colors uppercase tracking-wider"
                    >
                      Recalcular Acumulados do Mês
                    </button>
                  </form>
                </div>

              </div>

              {/* Histórico de Fechamentos */}
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-2.5 text-[10px] font-black uppercase text-slate-400">Mês/Ano</th>
                      <th className="px-4 py-2.5 text-[10px] font-black uppercase text-slate-400">Status</th>
                      <th className="px-4 py-2.5 text-[10px] font-black uppercase text-slate-400">Fechado em</th>
                      <th className="px-4 py-2.5 text-[10px] font-black uppercase text-slate-400">Ações / Detalhes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {finance.fechamentos && finance.fechamentos.map((f) => {
                      const isReaberto = !!f.reaberto_por;
                      return (
                        <tr key={f.id} className="hover:bg-slate-50/50">
                          <td className="px-4 py-2.5 text-xs font-black text-slate-700 font-mono">
                            {String(f.mes).padStart(2, "0")}/{f.ano}
                          </td>
                          <td className="px-4 py-2.5 text-xs">
                            {isReaberto ? (
                              <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[9px] font-bold">Reaberto</span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded text-[9px] font-bold">Fechado</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-[10px] text-slate-500">
                            {new Date(f.data_fechamento).toLocaleString("pt-BR")}
                          </td>
                          <td className="px-4 py-2.5">
                            {!isReaberto ? (
                              <button
                                type="button"
                                onClick={() => handleReabrirClick(f)}
                                className="text-[10px] bg-amber-600 hover:bg-amber-700 text-white font-bold px-2.5 py-1 rounded shadow-xs uppercase tracking-wider"
                              >
                                Reabrir
                              </button>
                            ) : (
                              <span className="text-[10px] text-slate-500 italic" title={f.motivo_reabertura}>
                                Motivo: {f.motivo_reabertura}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {(!finance.fechamentos || finance.fechamentos.length === 0) && (
                      <tr>
                        <td colSpan="4" className="px-4 py-6 text-center text-xs font-medium text-slate-400">
                          Nenhum fechamento registrado no sistema.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

            </div>
          )}

          {section === "compromisso" && (
            <div className="p-6 space-y-6">
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Nome do Tipo</th>
                      <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Status</th>
                      <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 text-right w-28">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs font-semibold text-slate-650">
                    {finance.tiposCompromisso && finance.tiposCompromisso.map((t) => (
                      <tr key={t.id} className="hover:bg-slate-50/50">
                        <td className="px-4 py-3 text-sm font-bold text-slate-800">
                          {t.nome}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border ${
                            t.ativo !== false
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                              : "bg-rose-50 border-rose-200 text-rose-700"
                          }`}>
                            {t.ativo !== false ? "Ativo" : "Desativado"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => openEditModal(t)}
                            className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                            title="Editar"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              const newStatus = !t.ativo;
                              if (!window.confirm(`Tem certeza que deseja ${newStatus ? 'reativar' : 'desativar'} o tipo de compromisso "${t.nome}"?`)) return;
                              try {
                                finance.setLoading(true);
                                await finance.updateTipoCompromisso(t.id, { ativo: newStatus });
                                alert(`Tipo de compromisso ${newStatus ? 'reativado' : 'desativado'} com sucesso!`);
                              } catch (e) {
                                alert("Erro: " + e.message);
                              } finally {
                                finance.setLoading(false);
                              }
                            }}
                            className="text-slate-400 hover:text-amber-600 transition-colors p-1"
                            title={t.ativo !== false ? "Desativar" : "Reativar"}
                          >
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(!finance.tiposCompromisso || finance.tiposCompromisso.length === 0) && (
                      <tr>
                        <td colSpan="3" className="px-4 py-8 text-center text-xs font-semibold text-slate-450">
                          Nenhum tipo de compromisso cadastrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── MODAL DE DIÁLOGO CADASTRO / EDIÇÃO ─────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 overflow-y-auto animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">
                {modalType === "create" ? "Novo Registro" : "Editar Registro"}
              </h4>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 flex-1 space-y-4">
              
              {/* Campo Nome Identificador (Comum a todos) */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                  Nome Identificador / Descritivo *
                </label>
                <input
                  type="text"
                  required
                  value={fieldName}
                  onChange={(e) => setFieldName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  placeholder="EX: ALUGUEL, SICREDI, ESCRITÓRIO"
                />
              </div>

              {/* Formulário Específico de Categorias */}
              {section === "categorias" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tipo *</label>
                      <select
                        value={fieldTipo}
                        onChange={(e) => setFieldTipo(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                      >
                        <option value="despesa">Despesa (Saída)</option>
                        <option value="receita">Receita (Entrada)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Classificação *</label>
                      <select
                        required
                        value={fieldClassificacao}
                        onChange={(e) => setFieldClassificacao(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                      >
                        <option value="" disabled>Selecione...</option>
                        {finance.classificacoes.map((c) => (
                          <option key={c.id} value={c.id}>{c.nome}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {finance.classificacoes.length === 0 && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 p-2.5 rounded-lg text-xs">
                      <ShieldAlert size={16} className="shrink-0 mt-0.5 text-amber-600" />
                      <span>
                        Nenhuma <strong>Classificação</strong> ativa encontrada. Vá na aba de Classificações para criar uma primeiro!
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Formulário Específico de Contas Bancárias */}
              {section === "contas" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nome do Banco</label>
                      <input
                        type="text"
                        value={fieldBanco}
                        onChange={(e) => setFieldBanco(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="EX: ITAÚ, BANCO DO BRASIL"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tipo da Conta</label>
                      <select
                        value={fieldContaTipo}
                        onChange={(e) => setFieldContaTipo(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                      >
                        <option value="corrente">Corrente</option>
                        <option value="poupanca">Poupança</option>
                        <option value="investimento">Investimento</option>
                        <option value="caixa_fisico">Caixa Físico</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Agência</label>
                      <input
                        type="text"
                        value={fieldAgencia}
                        onChange={(e) => setFieldAgencia(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                        placeholder="EX: 1234"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Conta Número</label>
                      <input
                        type="text"
                        value={fieldConta}
                        onChange={(e) => setFieldConta(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                        placeholder="EX: 98765-4"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Saldo Inicial (R$)</label>
                    <input
                      type="text"
                      value={fieldSaldoInicial}
                      onChange={(e) => setFieldSaldoInicial(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-bold font-mono text-right"
                      placeholder="0,00"
                    />
                  </div>
                </>
              )}

              {/* Botão de Toggle Ativo/Inativo (Comum exceto classificacoes) */}
              {section !== "classificacoes" && (
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="fieldAtivo"
                    checked={fieldAtivo}
                    onChange={(e) => setFieldAtivo(e.target.checked)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-550 border-slate-300 rounded"
                  />
                  <label htmlFor="fieldAtivo" className="text-xs font-bold text-slate-600 uppercase select-none cursor-pointer">
                    Registro ativo para lançamentos
                  </label>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <Check size={16} /> Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
